/**
 * Panel + session export helpers for InterviewFlow apps.
 */

export function buildPanelExportPayload({
  panelId,
  panel,
  slice,
  structuredOutput,
  governance,
}) {
  const opts = {
    redactEmail: Boolean(governance?.redactEmailInExports ?? governance?.redactEmail),
    redactPhone: Boolean(governance?.redactPhoneInExports ?? governance?.redactPhone),
  };
  const payload = {
    exportedAt: new Date().toISOString(),
    panelId,
    componentType: panel?.componentType || panel?.type,
    outputKey: panel?.outputKey,
    slice: slice ?? null,
    structuredOutput: structuredOutput && typeof structuredOutput === 'object' ? { ...structuredOutput } : null,
  };
  if (opts.redactEmail || opts.redactPhone) {
    payload.slice = redactExportValue(payload.slice, opts);
  }
  return payload;
}

function redactExportValue(val, opts) {
  if (typeof val === 'string') {
    let out = val;
    if (opts.redactEmail) out = out.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[redacted-email]');
    if (opts.redactPhone) out = out.replace(/(?:\+?\d{1,3}[-.\s]?)?(?:\(?\d{2,4}\)?[-.\s]?)?\d{3}[-.\s]?\d{2,4}[-.\s]?\d{2,6}/g, '[redacted-phone]');
    return out;
  }
  if (Array.isArray(val)) return val.map((v) => redactExportValue(v, opts));
  if (val && typeof val === 'object') {
    const next = {};
    Object.entries(val).forEach(([k, v]) => { next[k] = redactExportValue(v, opts); });
    return next;
  }
  return val;
}

export function downloadJsonInBrowser(filename, dataObj) {
  const blob = new Blob([JSON.stringify(dataObj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.replace(/[^\w.-]+/g, '_') || 'export.json';
  a.click();
  URL.revokeObjectURL(url);
}

/** Open a print dialog for a simple HTML rendering (Save as PDF in browser). */
export function printPanelAsPdf({ title, htmlBody }) {
  const win = window.open('', '_blank', 'noopener,noreferrer');
  if (!win) return false;
  win.document.write(`<!DOCTYPE html><html><head><title>${title || 'Export'}</title>
<style>body{font-family:system-ui,sans-serif;padding:24px;max-width:800px;margin:0 auto}
table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:8px;text-align:left}
h1{font-size:1.25rem}</style></head><body>
<h1>${title || 'InterviewFlow export'}</h1>
${htmlBody}
<p style="margin-top:24px;font-size:12px;color:#666">Exported ${new Date().toLocaleString()}</p>
</body></html>`);
  win.document.close();
  win.focus();
  win.print();
  return true;
}

export function panelSliceToPrintHtml(slice, componentType) {
  if (!slice) return '<p>No data</p>';
  if (componentType === 'rich_text') {
    const text = typeof slice === 'string' ? slice : (slice.content || JSON.stringify(slice));
    return `<div>${String(text).replace(/\n/g, '<br/>')}</div>`;
  }
  if (componentType === 'table' && slice.rows) {
    const cols = slice.columns || Object.keys(slice.rows[0] || {}).filter((k) => k !== 'id');
    const headers = cols.map((c) => (typeof c === 'object' ? c.headerName || c.field : c));
    const fields = cols.map((c) => (typeof c === 'object' ? c.field : c));
    const head = headers.map((h) => `<th>${h}</th>`).join('');
    const body = (slice.rows || []).map((row) => `<tr>${fields.map((f) => `<td>${row[f] ?? ''}</td>`).join('')}</tr>`).join('');
    return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
  }
  return `<pre>${JSON.stringify(slice, null, 2)}</pre>`;
}
